import { ContainerCopyBeforeDeleteService } from '@affine/core/components/delete-with-copy/container-copy-before-delete.service';
import { type Framework } from '@toeverything/infra';

import { WorkspaceServerService } from '../cloud';
import { DocCreateMiddleware } from '../doc/providers/doc-create-middleware';
import { AccessibleDocCacheStore } from '../doc/stores/accessible-doc-cache';
import { OfflineMutationOutboxService } from '../offline-mutation-outbox';
import { AccessibleFolderCacheStore, FolderStore } from '../organize';
import { GlobalState } from '../storage';
import { WorkspaceScope, WorkspaceService } from '../workspace';
import { FolderAssignmentDocCreateMiddleware } from './impls/folder-assignment-doc-create-middleware';
import { FolderApiService } from './services/folder-api';
import { FolderDocSyncService } from './services/folder-doc-sync';
import { FolderMutationOutboxService } from './services/folder-mutation-outbox';
import { WorkspaceAreaApiService } from './services/workspace-area-api';
import { WorkspaceAreaCacheStore } from './stores/workspace-area-cache';

/**
 * FolderApiModule - E035.3: Folder RBAC API
 *
 * Provides GraphQL-based folder management with permission checking.
 * Works alongside the existing Y.js-based local folder store.
 */

export type {
  FolderData,
  FolderGroupRole,
  FolderMateRole,
  FolderOwner,
  FolderPermissions,
  FolderRole,
  FolderWithDetails,
  GrantedFolderUser,
  GroupData,
  NamedMate,
  PaginatedFolderUsers,
  PermissionLifecyclePreflightInput,
  PermissionLifecyclePreflightResult,
  TrashedFolderData,
} from './services/folder-api';
export {
  FolderApiService,
  PermissionDenialReason,
  PermissionLifecycleOperation,
  PermissionPreflightTargetKind,
} from './services/folder-api';
export { FolderMutationOutboxService } from './services/folder-mutation-outbox';
export {
  moveFolderToTrashLocalFirst,
  type NavigationPanelFolderVisibility,
} from './services/folder-trash-local-first';
export {
  WorkspaceAreaApiService,
  type WorkspaceAreaKind,
  type WorkspaceAreaRef,
} from './services/workspace-area-api';
export { WorkspaceAreaCacheStore } from './stores/workspace-area-cache';
export { folderCreateErrorI18nKey } from './utils/folder-create-error';
export { folderMoveErrorI18nKey } from './utils/folder-move-error';
export {
  getRememberedFolderPermissions,
  rememberFolderPermissions,
} from './utils/folder-permission-registry';
export {
  NO_FOLDER_PERMISSIONS,
  OWNER_FOLDER_PERMISSIONS,
} from './utils/folder-permissions';

// Re-export FolderData as FolderType for backwards compatibility
export type { FolderData as FolderType } from './services/folder-api';

export function configureFolderApiModule(framework: Framework) {
  framework
    .scope(WorkspaceScope)
    .service(FolderApiService, [WorkspaceServerService])
    .service(WorkspaceAreaApiService, [WorkspaceServerService])
    .store(WorkspaceAreaCacheStore, [
      WorkspaceService,
      WorkspaceAreaApiService,
      WorkspaceServerService,
      GlobalState,
    ])
    .service(FolderMutationOutboxService, [
      WorkspaceService,
      FolderApiService,
      OfflineMutationOutboxService,
      AccessibleFolderCacheStore,
      AccessibleDocCacheStore,
      ContainerCopyBeforeDeleteService,
    ])
    .service(FolderDocSyncService, [
      WorkspaceService,
      FolderApiService,
      FolderMutationOutboxService,
      FolderStore,
      AccessibleFolderCacheStore,
    ])
    .impl(
      DocCreateMiddleware('folder-assignment'),
      FolderAssignmentDocCreateMiddleware,
      [
        WorkspaceService,
        FolderMutationOutboxService,
        AccessibleFolderCacheStore,
      ]
    );
}
